import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule, Leaf, Users, MapPin, Gift, X, Bot, BarChart3, Globe, Link, Shield, Tag } from 'lucide-angular';
import { Subscription } from 'rxjs';

import { ForumService } from '../../services/forum.service';
import { ForumLeadData } from '../../models/forum.models';
import { CurrentUserService } from '../../services/current-user.service';

@Component({
  selector: 'app-forum-lead-capture-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    Select,
    TranslocoModule,
    LucideAngularModule,
  ],
  templateUrl: './forum-lead-capture-modal.component.html',
  styleUrls: ['./forum-lead-capture-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ForumLeadCaptureModalComponent implements OnInit, OnDestroy {
  private forumService = inject(ForumService);
  private translocoService = inject(TranslocoService);
  private currentUserService = inject(CurrentUserService);
  private notificationService = inject(NotificationService);
  private fb = inject(FormBuilder);

  // Icons
  leafIcon = Leaf;
  usersIcon = Users;
  mapPinIcon = MapPin;
  giftIcon = Gift;
  xIcon = X;
  botIcon = Bot;
  barChartIcon = BarChart3;
  globeIcon = Globe;
  linkIcon = Link;
  shieldIcon = Shield;
  tagIcon = Tag;

  visible = false;
  isLoading = false;
  showSuccess = false;
  errorMessage = '';
  tripData: {tripName: string, co2: number} | null = null;

  // Options pour les dropdowns (initialisées après chargement des traductions)
  organizationTypeOptions: Array<{label: string, value: string}> = [];

  leadForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    company: ['', [Validators.required]],
    tripsPerYear: [''],
    organizationType: ['']
  });

  private subscription = new Subscription();

  ngOnInit(): void {
    // Attendre que les traductions soient chargées avant d'initialiser les options
    // Utiliser selectTranslateObject pour charger tout l'objet d'un coup
    this.subscription.add(
      this.translocoService.selectTranslateObject<Record<string, string>>('forum.modal.form.organizationTypes').subscribe((translations) => {
        this.initializeDropdownOptions(translations);
      })
    );

    // S'abonner aux changements de visibilité de la modale
    this.subscription.add(
      this.forumService.showForumModal$.subscribe(show => {
        this.visible = show;
        if (show) {
          this.resetForm();
          this.loadTripData();
        }
      })
    );

    // S'abonner aux données du trip
    this.subscription.add(
      this.forumService.currentTripData$.subscribe(data => {
        this.tripData = data;
      })
    );
  }

  /**
   * Initialise les options des dropdowns avec les traductions
   */
  private initializeDropdownOptions(translations: Record<string, string>): void {
    this.organizationTypeOptions = [
      { value: 'tour-operator', label: translations['tour-operator'] },
      { value: 'travel-agency', label: translations['travel-agency'] },
      { value: 'dmc', label: translations['dmc'] },
      { value: 'mice-organizer', label: translations['mice-organizer'] },
      { value: 'other', label: translations['other'] }
    ];
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Charge les données du trip depuis le service
   */
  private loadTripData(): void {
    const data = this.forumService.getCurrentTripData();
    if (data) {
      this.tripData = data;
    }
  }

  /**
   * Enables or disables all form fields
   */
  private toggleFormState(disabled: boolean): void {
    disabled ? this.leadForm.disable() : this.leadForm.enable();
  }

  /**
   * Submits the lead capture form
   */
  onSubmit(): void {
    if (this.leadForm.valid && !this.isLoading && this.tripData) {
      this.isLoading = true;
      this.toggleFormState(true);
      this.errorMessage = '';

      const formValue = this.leadForm.value;
      const forumLeadData: ForumLeadData = {
        email: formValue.email.trim(),
        company: formValue.company.trim(),
        tripName: this.tripData.tripName,
        co2Calculated: this.tripData.co2,
        tripsPerYear: formValue.tripsPerYear ? formValue.tripsPerYear.toString() : undefined,
        organizationType: formValue.organizationType || undefined,
        language: this.translocoService.getActiveLang()
      };

      this.forumService.createForumLead(forumLeadData).subscribe({
        next: () => {
          this.isLoading = false;
          this.toggleFormState(false);

          // Afficher un toast de succès
          this.notificationService.success(
            this.translocoService.translate('forum.modal.success.toast'),
          );

          // Fermer immédiatement la modale
          this.onClose();
        },
        error: (error) => {
          this.isLoading = false;
          this.toggleFormState(false);
          this.errorMessage = this.translocoService.translate('forum.modal.errorMessage');
          console.error('Erreur lors de la capture du lead:', error);
        }
      });
    }
  }

  /**
   * Ferme la modale
   */
  onClose(): void {
    this.forumService.hideForumModal();
    this.resetForm();
  }

  /**
   * Gère la fermeture via X ou Escape
   */
  onHide(): void {
    if (!this.isLoading) {
      this.onClose();
    }
  }

  /**
   * Ferme la modale avec le lien "Continuer l'exploration"
   */
  onSkip(): void {
    this.onClose();
  }

  /**
   * Remet à zéro le formulaire et les états
   */
  private resetForm(): void {
    this.leadForm.reset({
      email: '',
      company: '',
      tripsPerYear: '',
      organizationType: ''
    });
    this.showSuccess = false;
    this.errorMessage = '';
    this.isLoading = false;
  }

  /**
   * Méthode publique pour ouvrir la modale depuis l'extérieur
   */
  show(tripName: string, co2Calculated: number): void {
    this.forumService.showForumModal(tripName, co2Calculated);
  }

  /**
   * Formate l'affichage du CO2
   */
  formatCO2(co2: number): string {
    return co2.toFixed(2);
  }
}